import { useMemo, useState } from "react";
import { Link } from "react-router";
import { documents as docApi, orders as ordersApi } from "../api/client";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Icon, Spinner, StatePill } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateTimeShort, monthName, yyyymm } from "../lib/format";
import { usd } from "../lib/money";
import { supplierName } from "./Painel";

export function Documentos() {
  const { data, error, loading, reload } = useAsync(() => ordersApi.list({ page: 1 }), []);
  const items = data?.items ?? [];
  const months = useMemo(() => {
    const out: { key: string; label: string }[] = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
      out.push({ key: yyyymm(x), label: `${monthName(x)} ${x.getFullYear()}` });
    }
    return out;
  }, []);
  const [month, setMonth] = useState(months[0]!.key);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const monthLabel = months.find((m) => m.key === month)?.label ?? month;

  return (
    <Page title={t.documents.title}>
      <p className="ink2" style={{ fontSize: 13.5 }}>{t.documents.lead}</p>
      <div className="grid2">
        <div className="card pad stack s12">
          <h2>{t.documents.statements}</h2>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <div className="field" style={{ flexGrow: 1 }}>
              <label htmlFor="stmt-month">{t.common.date}</label>
              <div className="inp">
                <select id="stmt-month" value={month} onChange={(e) => setMonth(e.target.value)}>
                  {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
            </div>
            <a className="btn ghost" href={ordersApi.statementUrl(month)} target="_blank" rel="noopener">
              {Icon.download()}
              {fmt(t.documents.statementDownload, { month: monthLabel })}
            </a>
          </div>
          <p className="legal">{t.brand.fiscal}</p>
        </div>
        <div className="card pad stack s12">
          <h2>{t.documents.exportAll}</h2>
          <div className="row" style={{ alignItems: "flex-end" }}>
            <div className="field">
              <label htmlFor="csv-from">{t.common.dateFrom}</label>
              <div className="inp"><input id="csv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            </div>
            <div className="field">
              <label htmlFor="csv-to">{t.common.dateTo}</label>
              <div className="inp"><input id="csv-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            </div>
            <a className="btn ghost" href={ordersApi.exportCsvUrl({ from: from || undefined, to: to || undefined })} download>
              {Icon.download()}
              {t.common.export}
            </a>
          </div>
          <span className="muted small">{t.common.exportCsvHint}</span>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h2>{t.documents.byOrder}</h2></div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : items.length === 0 ? (
          <Empty body={t.documents.empty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.documents.colOrder}</th>
                  <th>{t.documents.colSupplier}</th>
                  <th>{t.common.status}</th>
                  <th>{t.documents.colDocs}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const docs = o.documents ?? [];
                  const invoice = docs.find((d) => d.kind === "invoice");
                  const receipt = docs.find((d) => d.kind === "receipt");
                  const fx = docs.find((d) => d.kind === "fx_contract");
                  const settled = o.state === "settled";
                  return (
                    <tr key={o.id}>
                      <td>
                        <div className="cell2">
                          <Link to={`/pagamentos/${o.id}`} className="mono" style={{ fontWeight: 600, textDecoration: "none", color: "var(--ink)" }}>{o.id}</Link>
                          <span>{dateTimeShort(o.settledAt ?? o.createdAt)} · {usd(o.usdCents)}</span>
                        </div>
                      </td>
                      <td>{supplierName(o)}</td>
                      <td><StatePill state={o.state} /></td>
                      <td>
                        <div className="row" style={{ gap: 14 }}>
                          <a className="linkbtn" href={invoice ? docApi.url(invoice.id) : ordersApi.documentUrl(o.id, "invoice")} target="_blank" rel="noopener">{Icon.download()}{t.documents.invoice}</a>
                          {(settled || receipt) && <a className="linkbtn" href={receipt ? docApi.url(receipt.id) : ordersApi.documentUrl(o.id, "receipt")} target="_blank" rel="noopener">{Icon.download()}{t.documents.receipt}</a>}
                          {(settled || fx) && <a className="linkbtn" href={fx ? docApi.url(fx.id) : ordersApi.documentUrl(o.id, "fx_contract")} target="_blank" rel="noopener">{Icon.download()}{t.documents.fxContract}</a>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Page>
  );
}
